import type { RouteLocationRaw } from 'vue-router';

/**
 * How many items one "Ask AI" action may reference. The chat resolves every reference
 * on every turn, so a wide selection is both slow and rarely what the user meant — they
 * pick the handful they actually want to talk about.
 */
export const MAX_ASK_AI_ITEMS = 5;

/**
 * Items the general chat can be pointed at. Vault directories are absent on purpose:
 * the backend resolves references to concrete file revisions and has no field for a folder.
 */
export interface AskAiSelection {
  fileUids?: string[];
  contractUids?: string[];
  contractFileUids?: string[];
}

export function askAiItemCount(selection: AskAiSelection): number {
  return (
    (selection.fileUids?.length ?? 0) +
    (selection.contractUids?.length ?? 0) +
    (selection.contractFileUids?.length ?? 0)
  );
}

/**
 * Route to a fresh chat pre-loaded with `selection` as composer context — the
 * home screen's chat, which is where the general chat lives. The uid query
 * params keep the names the old `/gaia-chat` route used, so a link that
 * predates the move still seeds the same way.
 */
export function askAiRoute(selection: AskAiSelection): RouteLocationRaw {
  const { fileUids = [], contractUids = [], contractFileUids = [] } = selection;
  return {
    path: '/',
    query: {
      chat: '1',
      // Distinct each time so asking from two different rows re-seeds the
      // composer instead of being treated as the same location.
      new_chat: String(Date.now()),
      ...(fileUids.length > 0 ? { vault_files: fileUids.join(',') } : {}),
      ...(contractUids.length > 0 ? { contracts: contractUids.join(',') } : {}),
      ...(contractFileUids.length > 0 ? { contract_files: contractFileUids.join(',') } : {}),
    },
  };
}

/** Message for a selection that exceeds the cap, or null when it's fine to navigate. */
export function askAiLimitWarning(selection: AskAiSelection): string | null {
  const count = askAiItemCount(selection);
  if (count === 0) return 'Nothing to ask about — select at least one item.';
  if (count > MAX_ASK_AI_ITEMS) {
    return `Ask AI takes at most ${MAX_ASK_AI_ITEMS} items at a time — you selected ${count}.`;
  }
  return null;
}
